//@ts-nocheck
import { useContext, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useParams } from "@tanstack/react-router";
import { usePlaceList } from "@/services/usePlaceList";
import { useProductList } from "@/services/useProductList";
import { VelContext } from "@/contexts/VelContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NEW_BACKOFFICE_URL } from "@/lib/constants";

import PlaceDetail from '@/components/place/PlaceDetail'
import { Separator } from "@radix-ui/react-separator";
import Badge from "@/components/ui/Badge";
import ProductOnAirFrance from "@/components/place/ProductOnAirFrance";
import ProductSharedVel from "@/components/place/ProductSharedVel";
import PlaceSyncInfo from "@/components/place/PlaceSyncInfo";
import { useSubscriptionTypes } from "@/services/useSubscriptionType";

export const Route = createFileRoute("/vel/$velId/place/$placeId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { placeId, velId } = useParams({ strict: false });
  const productList = useProductList({ placeId });
  const { data: products, isFetching } = productList;
  const places = usePlaceList({ velId });
  const { data: subscriptionTypes } = useSubscriptionTypes({ velId: Number(velId) });
  const vels = useContext(VelContext);

  useEffect(() => {
    if (vels) {
      const vel = vels.find((v) => v.id === Number(velId));
      if (vel?.migrated === 1) {
        window.location.href = `${NEW_BACKOFFICE_URL}/vel/${velId}`;
      }
    }
  }, [vels, velId]);

  const selectedPlace = (places.data || []).find((p) => p.msdsId === Number(placeId));

  return selectedPlace && (
    <div className="flex h-screen">
      <div className="w-5/6 bg-white m-3 p-3 rounded-lg overflow-y-auto">
        <PlaceDetail place={selectedPlace} />
        <PlaceSyncInfo place={selectedPlace} />
        <Tabs defaultValue={'subscription'}>
          <TabsList className="w-full mt-4">
            <TabsTrigger value="subscription">Subscriptions</TabsTrigger>
            <TabsTrigger value="counter">Carnets</TabsTrigger>
            <TabsTrigger value="subscription_option">Options</TabsTrigger>
            <TabsTrigger value="regular">Boutique</TabsTrigger>
            <TabsTrigger value="family">Famille</TabsTrigger>
          </TabsList>
          {(isFetching) && <p>Loading...</p>}
          {(!isFetching && !products) && <p>No items available</p>}
          {!isFetching &&
            products &&
            ["subscription", "counter", "subscription_option", "regular", "family"].map((t) => {
              return (
                <TabsContent value={t} key={t}>
                  <ProductList place={selectedPlace} products={products.filter((p) => p.data?.type === t)} subscriptionTypes={subscriptionTypes}/>
                </TabsContent>
              );
            })}
        </Tabs>
      </div>
    </div>
  );
}

function ProductList({ products, place, subscriptionTypes }) {
  if (!products.length)
    return (
      <div className="mb-4 pt-4 mt-4">
        <h2>Aucun produit dans cette section</h2>
      </div>
    );

  const notConfigured = [];
  const desactivated = [];
  const actives = [];
  const notYet = [];
  const noMore = [];

  products.forEach((p) => {
    if( !p.enabled ) {
      desactivated.push(p);
      return;
    }
    if( !p.description ) {
      notConfigured.push(p);
      return;
    }
    if (p.data.offer.validFrom && new Date(p.data.offer.validFrom).getTime() > Date.now()) {
      notYet.push(p);
      return;
    }
    if (p.data.offer.validThrough && new Date(p.data.offer.validThrough).getTime() < Date.now()) {
      noMore.push(p);
      return;
    }
    actives.push(p);
  });

  return (
    <>
      {notConfigured.length !== 0 && (
        <>
          <div className="mb-4 mt-4">
            <h2>Non configurées <Badge variant="orange">{notConfigured.length}</Badge></h2>
            <Separator className="border-t border-gray-300" />
          </div>
          {notConfigured.map((p) => {
            return <Product key={p.data["@id"]} place={place} product={p} validFrom={true} validThrough={true} subscriptionTypes={subscriptionTypes}/>;
          })}
        </>
      )}

      {actives.length !== 0 && (
        <>
        <div className="mb-4 mt-4">
          <h2>Actives <Badge variant="green">{actives.length}</Badge></h2>
          <Separator className="border-t border-gray-300" />
        </div>
        {actives.map((p) => {
          return <Product key={p.data["@id"]} place={place} product={p} validFrom={true} validThrough={true} subscriptionTypes={subscriptionTypes} />;
        })}
        </>
      )}
      {notYet.length !== 0 && (
        <>
          <div className="mb-4 mt-4">
            <h2>A venir <Badge variant="gray">{notYet.length}</Badge></h2>
            <Separator className="border-t border-gray-300" />
          </div>
          {notYet.map((p) => {
            return <Product key={p.data["@id"]} place={place} product={p} validFrom={false} validThrough={true} subscriptionTypes={subscriptionTypes} />;
          })}
        </>
      )}
      {noMore.length !== 0 && (
        <>
          <div className="mb-4 mt-4">
            <h2>Finis <Badge variant="gray">{noMore.length}</Badge></h2>
            <Separator className="border-t border-gray-300" />
          </div>
          {noMore.map((p) => {
            return <Product key={p.data["@id"]} place={place} product={p} validFrom={true} validThrough={false} subscriptionTypes={subscriptionTypes} />;
          })}
        </>
      )}
      {desactivated.length !== 0 && (
        <>
          <div className="mb-4 mt-4">
            <h2>Non activées <Badge variant="gray">{desactivated.length}</Badge></h2>
            <Separator className="border-t border-gray-300" />
          </div>
          {desactivated.map((p) => {
            return <Product key={p.data["@id"]} place={place} product={p} validFrom={true} validThrough={true} subscriptionTypes={subscriptionTypes} />;
          })}
        </>
      )}
    </>
  );
}

function Product({ place, product, validFrom, validThrough, subscriptionTypes }) {
  const { velId } = useParams({ strict: false });
  const context = useContext(VelContext);
  if (!context) return null;

  const vel = context.find((v) => v.id === +velId);
  if (vel.code.startsWith("onair")) {
    return <ProductOnAirFrance place={place} product={product} validFrom={validFrom} validThrough={validThrough} />;
  }

  return <ProductSharedVel place={place} product={product} validFrom={validFrom} validThrough={validThrough} subscriptionTypes={subscriptionTypes} />;
}


